import React, { useEffect, useRef, useState, useMemo } from 'react'
import { rem } from 'polished'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import Heading from 'components/Luxkit/Typography/Heading'
import TextButton from 'components/Luxkit/Button/TextButton'
import Image from 'components/Common/Image'
import LineExpandArrowsAltIcon from 'components/Luxkit/Icons/line/LineExpandArrowsAltIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useToggle from 'hooks/useToggle'
import styled from 'styled-components'
import CabinTile, { CabinsToDisplay } from './CruiseCabinTile'
import MonthTabButtonsGroup from 'components/OfferPage/TourV2/Departures/MonthTabs/MonthTabButtonsGroup'
import Caption from 'components/Luxkit/Typography/Caption'

const GridContainer = styled.div`
  width: 100%;
  align-items: flex-start;
  justify-content: center;
  padding-bottom: ${rem(20)};
  margin-top: ${rem(20)};

  display: block;
  grid-column-gap: ${rem(20)};
  grid-template-columns: 11fr 17fr;

  ${mediaQueryUp.tablet} {
    display: grid;
  }
`

const CabinsContainer = styled.div`
  position: sticky;
  top: ${rem(5)};
  margin-top: ${rem(16)};
  .mt-8 {
    margin-top: ${rem(8)};
  }

  > div:last-child {
    margin-top: ${rem(20)};
    margin-bottom: 0;
  }

  &.has-sticky-header {
    top: ${rem(120)};
  }

  ${mediaQueryUp.tablet} {
    margin-top: 0;
  }
`

const DeckContainer = styled.div``

const Deck = styled.div`
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.six};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${rem(20)};
  height: 100%;
`

const DeckImageContainer = styled.div`
  margin-top: ${rem(12)};
  padding-top: ${rem(12)};
  border-top: ${rem(1)} solid ${props => props.theme.palette.neutral.six};
  height: 100%;
  position: relative;
  max-height: ${rem(600)};
  overflow: hidden;

  &.expanded {
    max-height: none;
    overflow: visible;

    .expand-map-button {
      display: none;
    }
  }

  ${mediaQueryUp.tablet} {
    max-height: none;
  }
`

const DeckImage = styled(Image)`
  width: 100%;
`

const ExpandMapButton = styled(TextButton)`
  position: absolute;
  right: ${rem(8)};
  bottom: ${rem(8)};
  background-color: rgba(0, 0, 0, 0.7);
  display: block;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const cabinShowOrder = ['Inside', 'Oceanview', 'Balcony', 'Suite']

interface Props {
  ship: App.CruiseShip;
  showPackageOptions: () => void;
  hasGotToOverview: boolean;
}
function CruiseDecksAndCabins(props: Props) {
  const { ship, showPackageOptions, hasGotToOverview } = props
  const [cabinsToShow, setCabinsToShow] = useState<CabinsToDisplay>({})
  const [selectedDeck, setSelectedDeck] = useState<App.CruiseShipDeck>()
  const [isMapExpanded, toggleExpandMap] = useToggle(false)

  const shipDecks = useMemo(() => {
    const decksByNumber: { [key: string]: App.CruiseShipDeck } = {}
    const decks = ship.decks || []

    // unique decks by deckNumber
    decks.forEach((obj) => decksByNumber[obj.deckNumber] = obj)
    return Object.values(decksByNumber).sort((a, b) => a.deckNumber - b.deckNumber)
  }, [ship.decks])

  const groupImagesByCabinCategory = (categories: App.CruiseShipCabinCategory[]): App.CruiseGroupedCabinCategories => {
    const groups = categories.reduce((result, category) => {
      const group = category.cabinType
      result[group] ?
        result[group].push(category) :
        result[group] = [category]
      return result
    }, {})

    return groups
  }

  const groupColorsByCabinCategory = (categories: App.CruiseShipCabinCategory[]): App.CruiseGroupedCabinCategoryColors => {
    const groups = categories.reduce((result, category) => {
      if (category.color) {
        const group = category.cabinType
        result[group] ?
          result[group].push(category.color) :
          result[group] = [category.color]
      }
      return result
    }, {})

    return groups
  }

  const cabinFiltersRef = useRef<HTMLDivElement>()

  const selectDeck = (deckId: string): void => {
    setSelectedDeck(shipDecks.find(deck => deck.id === deckId))
  }

  useEffect(() => {
    const getCabinsForCarousel = (categories: App.CruiseShipCabinCategory[]): CabinsToDisplay => {
      const groupedImages = groupImagesByCabinCategory(categories)
      const groupedColors = groupColorsByCabinCategory(categories)
      const gallery: CabinsToDisplay = {}
      for (const key in groupedImages) {
        groupedImages[key].forEach((item) => {
          if (!gallery[item.cabinType]) {
            gallery[item.cabinType] = {
              images: [],
              colors: groupedColors[item.cabinType],
              info: {
                ...item,
              },
            }
          }
          if (!gallery[item.cabinType].images.find(item2 => item2.id === item.imageId)) {
            gallery[item.cabinType].images.push({ id: item.imageId, type: 'image' })
          }
        })
      }
      return gallery
    }
    const gallery = getCabinsForCarousel(ship.cabinCategories)
    setCabinsToShow(gallery)
  }, [ship.cabinCategories])

  useEffect(() => {
    setSelectedDeck(shipDecks[0]) // default on load
  }, [shipDecks])

  return (
    <>
      <MonthTabButtonsGroup ref={cabinFiltersRef}>
        {shipDecks.map((deck) => (
          <FilterChip
            onClick={() => selectDeck(deck.id)}
            selected={deck.id === selectedDeck?.id}
            key={deck.id}
            size="large"
          >{deck.name}</FilterChip>
        ))}
      </MonthTabButtonsGroup>

      <GridContainer>
        <DeckContainer data-testid="deck-image">
          <Deck>
            <Heading variant="heading5" align="center">{selectedDeck?.name}</Heading>
            <DeckImageContainer
              className={isMapExpanded && 'expanded'}
            >
              <DeckImage
                id={selectedDeck?.imageId}
                quality="best"
                loading="lazy"
                alt={selectedDeck?.name}
              />

              <ExpandMapButton
                kind="tertiary"
                variant="ghost"
                startIcon={<LineExpandArrowsAltIcon />}
                className="expand-map-button"
                onClick={toggleExpandMap}
              >
                Expand Map
              </ExpandMapButton>
            </DeckImageContainer>
          </Deck>
        </DeckContainer>

        <CabinsContainer className={hasGotToOverview && 'has-sticky-header'} data-testid="cabin-list">
          {cabinShowOrder.map((key) => (cabinsToShow[key] &&
            <CabinTile
              key={cabinsToShow[key].info.imageId}
              cabin={cabinsToShow[key]}
              cabinCategories={ship.cabinCategories}
              showPackageOptions={showPackageOptions}
            />
          ))}
          <Caption variant="medium">Note: cabin photos are provided for illustrative purposes. The actual cabin may differ.</Caption>
        </CabinsContainer>
      </GridContainer>
    </>
  )
}

export default CruiseDecksAndCabins
